import React, { useState, useEffect } from 'react';
import { TrendingUp, Eye, Users, BarChart3, Activity, Clock, Target, Download } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';
const getToken = () => localStorage.getItem('accessToken') || localStorage.getItem('token');

interface CampaignStats {
  totalViews?: number;
  totalSubscribers?: number;
  conversionRate?: string;
  totalCampaigns?: number;
}

interface Campaign {
  id: string;
  _id?: string;
  name?: string;
  viewCount?: number;
  subscriberCount?: number;
  status?: string;
}

interface AnalyticsTabProps {
  stats: CampaignStats;
  campaigns: Campaign[];
}

export default function AnalyticsTab({ stats, campaigns }: AnalyticsTabProps) {
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/enterprise/analytics/dashboard?range=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAnalyticsData(data.data || data);
      } else {
        setError(data.error || 'Failed to load analytics');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const viewsOverTime = analyticsData?.viewsOverTime || [];
  const maxViews = Math.max(...viewsOverTime.map((d: any) => d.views || 0), 1);

  return (
    <div>
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h3 className="analytics-title">
            <TrendingUp size={20} />
            Advanced Analytics
          </h3>
          <p className="analytics-subtitle">Track your campaign performance and growth</p>
        </div>
        <div className="analytics-time-range">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`analytics-range-btn ${timeRange === range ? 'analytics-range-btn--active' : ''}`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="analytics-error">
          ❌ {error}
          <button onClick={fetchAnalytics} className="analytics-retry-btn">Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="analytics-loading">
          <Activity size={24} />
          <p>Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="analytics-stats-grid">
            <div className="analytics-stat-card" style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
              <Eye size={22} color="#16a34a" />
              <div className="analytics-stat-value" style={{ color: '#16a34a' }}>
                {(stats.totalViews || analyticsData?.totalViews || 0).toLocaleString()}
              </div>
              <div className="analytics-stat-label">Total Views</div>
            </div>
            <div className="analytics-stat-card" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
              <Users size={22} color="#2563eb" />
              <div className="analytics-stat-value" style={{ color: '#2563eb' }}>
                {(stats.totalSubscribers || analyticsData?.totalSubscribers || 0).toLocaleString()}
              </div>
              <div className="analytics-stat-label">Subscribers</div>
            </div>
            <div className="analytics-stat-card" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
              <Target size={22} color="#d97706" />
              <div className="analytics-stat-value" style={{ color: '#d97706' }}>
                {stats.conversionRate || analyticsData?.conversionRate || '0'}%
              </div>
              <div className="analytics-stat-label">Conversion Rate</div>
            </div>
            <div className="analytics-stat-card" style={{ background: '#faf5ff', borderColor: '#e9d5ff' }}>
              <BarChart3 size={22} color="#7c3aed" />
              <div className="analytics-stat-value" style={{ color: '#7c3aed' }}>
                {stats.totalCampaigns || campaigns.length || analyticsData?.totalCampaigns || 0}
              </div>
              <div className="analytics-stat-label">Total Campaigns</div>
            </div>
          </div>

          {/* Views Chart */}
          <div className="analytics-chart">
            <h4 className="analytics-chart-title">
              <BarChart3 size={16} />
              Campaign Views ({timeRange === '7d' ? '7-Day' : timeRange === '30d' ? '30-Day' : '90-Day'} Trend)
            </h4>
            {viewsOverTime.length === 0 ? (
              <div className="analytics-chart-empty">
                <Activity size={32} />
                <p>No view data available yet</p>
              </div>
            ) : (
              <div className="analytics-chart-bars">
                {viewsOverTime.map((day: any, i: number) => (
                  <div key={i} className="analytics-chart-bar-item">
                    <span className="analytics-chart-bar-value">{day.views || 0}</span>
                    <div
                      className="analytics-chart-bar"
                      style={{
                        height: `${((day.views || 0) / maxViews) * 100}%`,
                        background: 'linear-gradient(180deg, #8b5cf6, #6366f1)',
                      }}
                    />
                    <span className="analytics-chart-bar-label">{day.date || day.label || `Day ${i + 1}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Campaign Table */}
          <div className="analytics-table-container">
            <h4 className="analytics-table-title">
              <Target size={16} />
              Campaign Performance
              <button 
  className="analytics-export-btn"
  onClick={() => {
    const token = getToken();
    fetch(`${API_URL}/enterprise/analytics/export`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }}
>
  <Download size={14} /> Export
</button>
            </h4>
            <div className="analytics-table-wrapper">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Views</th>
                    <th>Subscribers</th>
                    <th>Conversion</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="analytics-table-empty">
                        <Activity size={20} />
                        <p>No campaigns yet. Create one to see analytics.</p>
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c) => {
                      const conv = (c.viewCount || 0) > 0
                        ? (((c.subscriberCount || 0) / (c.viewCount || 1)) * 100).toFixed(1)
                        : '0';
                      return (
                        <tr key={c.id || c._id}>
                          <td className="analytics-table-name">{c.name || 'Untitled'}</td>
                          <td>{(c.viewCount || 0).toLocaleString()}</td>
                          <td>{(c.subscriberCount || 0).toLocaleString()}</td>
                          <td>
                            <span className="analytics-conversion-badge">
                              {conv}%
                            </span>
                          </td>
                          <td>
                            <span className={`analytics-status-badge analytics-status--${c.status || 'draft'}`}>
                              {c.status || 'draft'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Insights */}
          {analyticsData?.insights && (
            <div className="analytics-insights">
              <h4 className="analytics-insights-title">
                <Target size={16} />
                AI-Powered Insights
              </h4>
              <ul className="analytics-insights-list">
                {analyticsData.insights.map((insight: string, i: number) => (
                  <li key={i}>{insight}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}